import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  DataTable,
  DataTableColumnHeader,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  StatusUI,
  statusDisplay,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  useAppForm,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { dateSchema, neverFunc } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import countries from "i18n-iso-countries";
import { MoreHorizontal, X } from "lucide-react";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { FaInfoCircle, FaTrash } from "react-icons/fa";
import {
  RiAddLine,
  RiBuilding2Line,
  RiFilter3Line,
  RiGlobeLine,
  RiPriceTagLine,
} from "react-icons/ri";
import { typeid } from "typeid-js";
import { useDebounceCallback } from "usehooks-ts";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { externalBankAccountDataSourceValues } from "~/gql/client/enumValues";
import type {
  AdminExternalBankAccountDataInstitutionRowFragment,
  ExternalBankAccountDataInstitutionFilter,
  ExternalBankAccountDataSource,
  UpsertExternalBankAccountDataInstitutionInput,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/external-providers")({
  head: () => opsHead("External Providers"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { useSearchParams } = useRouter();
  const [upsertRow] = useMutation(
    graphql(`
      mutation SetExternalInstitutionRemovedAt($input: UpsertExternalBankAccountDataInstitutionInput!) {
          upsertExternalBankAccountDataInstitution(input: $input) {
              institution {
                  ...AdminExternalBankAccountDataInstitutionRow
              }
          }
      }
    `),
  );

  const [editingRow, setEditingRow] =
    useState<AdminExternalBankAccountDataInstitutionRowFragment | null>(null);
  const [upsertDialogOpen, setUpsertDialogOpen] = useState(false);

  const columns = useMemo<
    ColumnDef<
      AdminExternalBankAccountDataInstitutionRowFragment & { id: string }
    >[]
  >(
    () => [
      {
        id: "displayName",
        accessorKey: "displayName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Display name" />
        ),
        enableSorting: false,
      },
      {
        id: "source",
        accessorFn: (row) => {
          switch (row.__typename) {
            case "AkahuExternalBankAccountDataInstitution":
              return "Akahu";
            case "QuilttExternalBankAccountDataInstitution":
              return "Quiltt";
            case "SaltEdgeExternalBankAccountDataInstitution":
              return "Salt Edge";
            case "SissExternalBankAccountDataInstitution":
              return "Siss";
            case "SkriptExternalBankAccountDataInstitution":
              return "Skript";
            case "StripeExternalBankAccountDataInstitution":
              return "Stripe";
            case "MercuryExternalBankAccountDataInstitution":
              return "Mercury";
            case "BrexExternalBankAccountDataInstitution":
              return "Brex";
            case "RampExternalBankAccountDataInstitution":
              return "Ramp";
            case "PlaidExternalBankAccountDataInstitution":
              return "Plaid";
            case "JpmcExternalBankAccountDataInstitution":
              return "JPMC";
            default:
              return neverFunc(row);
          }
        },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="External Source" />
        ),
        enableSorting: false,
      },
      {
        id: "externalName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="External Name" />
        ),
        accessorKey: "externalName",
        enableSorting: false,
      },
      {
        id: "externalId",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="External ID" />
        ),
        accessorFn: (row) => row.externalId ?? "-",
        enableSorting: false,
      },
      {
        id: "logoUrl",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Logo" />
        ),
        cell: ({ row }) => {
          if (!row.original.logoUrl) {
            return "-";
          }

          return (
            <img className="size-6" alt="logo" src={row.original.logoUrl} />
          );
        },
        enableSorting: false,
      },
      {
        id: "countryCode",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Country code" />
        ),
        cell: ({ row }) => {
          return row.original.iso3CountryCode ?? "-";
        },
        enableSorting: false,
      },
      {
        id: "status",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ row }) => {
          return (
            <StatusUI status={row.original.removedAt ? "INACTIVE" : "ACTIVE"} />
          );
        },
        enableSorting: false,
      },
      {
        id: "actions",
        header: () => <div className="sr-only">Actions</div>,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="secondary">
                <MoreHorizontal className="size-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => {
                  setEditingRow(row.original);
                  setUpsertDialogOpen(true);
                }}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  const source = typenameToSource(row.original.__typename);

                  const removedAt = row.original.removedAt ? null : new Date();

                  upsertRow({
                    optimisticResponse: {
                      __typename: "Mutation",
                      upsertExternalBankAccountDataInstitution: {
                        __typename:
                          "UpsertExternalBankAccountDataInstitutionPayload",
                        institution: {
                          __typename: row.original.__typename,
                          id: row.original.id,
                          displayName: row.original.displayName,
                          externalName: row.original.externalName,
                          removedAt,
                          logoUrl: row.original.logoUrl,
                          externalId: row.original.externalId,
                          iso3CountryCode: row.original.iso3CountryCode,
                        },
                      },
                    },
                    variables: {
                      input: {
                        id: row.original.id,
                        displayName: row.original.displayName,
                        externalName: row.original.externalName,
                        removedAt,
                        logoUrl: row.original.logoUrl,
                        externalId: row.original.externalId,
                        iso3CountryCode: row.original.iso3CountryCode,
                        source,
                      },
                    },
                    onCompleted(data) {
                      toast({
                        kind: "success",
                        title: `${data.upsertExternalBankAccountDataInstitution.institution.displayName} ${data.upsertExternalBankAccountDataInstitution.institution.removedAt ? "activated" : "deactivated"}`,
                        autoHide: true,
                      });
                    },
                    onError() {
                      toast({
                        kind: "error",
                        title: "Something went wrong",
                        autoHide: true,
                      });
                    },
                  });
                }}
              >
                {row.original.removedAt ? "Activate" : "Deactivate"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        enableSorting: false,
      },
    ],
    [upsertRow],
  );

  const [filter, setFilter] = useQueryPersistedStateWithSchema({
    useSearchParams,
    schema: z.object({
      active: z.boolean().nullish(),
      sources: z.enum(externalBankAccountDataSourceValues).array().nullish(),
      searchTerm: z.string().nullish(),
      iso3CountryCodes: z.string().nullish().array().nullish(),
    }),
    defaultState: {},
  });

  const baseInput = {
    first: 20,
    filter,
  };

  const { fetchMore, loading, data, networkStatus } = useQuery(
    graphql(`
    query AdminExternalInstitutions(
      $after: String,
      $first: Int!,
      $filter: ExternalBankAccountDataInstitutionFilter!,
    ) {
      externalBankAccountDataInstitutions(
        after: $after,
        first: $first,
        filter: $filter,
        distinct: false,
      ) {
        pageInfo {
          endCursor
          hasNextPage
        }
        institutions {
            ...AdminExternalBankAccountDataInstitutionRow
        }
      }
    }

    fragment AdminExternalBankAccountDataInstitutionRow on ExternalBankAccountDataInstitution {
      id
      displayName
      externalName
      logoUrl
      externalId
      removedAt
      iso3CountryCode
    }
  `),
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const nextCursor = data?.externalBankAccountDataInstitutions?.pageInfo
    .hasNextPage
    ? (data.externalBankAccountDataInstitutions.pageInfo.endCursor ?? undefined)
    : undefined;

  const rows = data?.externalBankAccountDataInstitutions?.institutions ?? [];

  return (
    <AdminPageContainer testId="admin-payouts">
      <AdminPageHeader
        title="External Providers"
        subtitle="Financial institutions available for account linking with Primary."
        buttons={
          <div className="flex gap-2">
            <Button
              leftIcon={<RiAddLine />}
              onClick={() => setUpsertDialogOpen(true)}
              variant="outline"
            >
              Add Provider
            </Button>
          </div>
        }
      />
      <div className="flex flex-col gap-4">
        <FilterBar filter={filter} onFilterChange={setFilter} />
        <DataTable
          data={rows}
          columns={columns}
          isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
          manualPagination={true}
          hidePagination={true}
          pageSize={10_000}
          className="overflow-hidden border"
        />
        <div className="mt-4 flex w-full flex-row justify-center">
          {nextCursor ? (
            <Button
              onClick={() =>
                fetchMore({ variables: { ...baseInput, after: nextCursor } })
              }
              variant="outline"
              disabled={loading}
              loading={loading || networkStatus === NetworkStatus.fetchMore}
            >
              Load more
            </Button>
          ) : (
            !!rows.length && (
              <p className="text-secondary text-sm">No more data</p>
            )
          )}
        </div>
      </div>
      <UpsertRowDialog
        open={upsertDialogOpen}
        onOpenChange={(open) => {
          setUpsertDialogOpen(open);
          if (!open && editingRow) {
            setEditingRow(null);
          }
        }}
        row={editingRow}
        onSubmit={async (input) => {
          await upsertRow({
            variables: {
              input,
            },
            refetchQueries: editingRow ? undefined : "active",
            awaitRefetchQueries: true,
            onCompleted() {
              toast({
                kind: "success",
                title: `Provider ${editingRow ? "updated" : "created"}`,
                autoHide: true,
              });
              setUpsertDialogOpen(false);
              if (editingRow) {
                setEditingRow(null);
              }
            },
            onError() {
              toast({
                kind: "error",
                title: "Something went wrong",
                autoHide: true,
              });
            },
          });
        }}
      />
    </AdminPageContainer>
  );
}

function FilterBar({
  filter,
  onFilterChange,
}: {
  filter: ExternalBankAccountDataInstitutionFilter;
  onFilterChange: (_: ExternalBankAccountDataInstitutionFilter) => void;
}) {
  const searchTermRef = useRef<HTMLInputElement | null>(null);
  const setSearchTermDebounced = useDebounceCallback(
    (searchTerm: string) =>
      onFilterChange({
        ...filter,
        searchTerm: searchTerm?.length ? searchTerm : undefined,
      }),
    200,
  );
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>();
  let filterCount = 0;
  if (!!filter.searchTerm) {
    filterCount++;
  }
  if (!!filter.active) {
    filterCount++;
  }
  if (!!filter.sources?.length) {
    filterCount++;
  }

  const resetFilter = () => {
    onFilterChange({
      searchTerm: undefined,
      sources: undefined,
      active: undefined,
      iso3CountryCodes: undefined,
    });
    if (searchTermRef.current) {
      searchTermRef.current.value = "";
    }
  };

  const statusOptions = ["ACTIVE" as const, "INACTIVE" as const].map((s) => ({
    value: s,
    label: statusDisplay[s].label,
  }));

  let statusFilterValue: Array<"ACTIVE" | "INACTIVE"> = [];
  if (filter.active === true) {
    statusFilterValue = ["ACTIVE"];
  } else if (filter.active === false) {
    statusFilterValue = ["INACTIVE"];
  }

  const countryOptions: Array<{ label: string; value: string | null }> =
    Object.entries(countries.getNames("en")).map(
      ([alpha2Code, friendlyCountry]) => ({
        label: friendlyCountry,
        value: countries.alpha2ToAlpha3(alpha2Code) ?? "",
      }),
    );
  countryOptions.push({
    label: "Global",
    value: null,
  });
  countryOptions.sort((a, b) => a.label.localeCompare(b.label));

  const sourceOptions = getSourceOptions();

  return (
    <div className="flex w-full flex-row items-center">
      <div className="flex flex-grow flex-row gap-2">
        <Popover open={open} onOpenChange={(open) => setOpen(open)}>
          <PopoverTrigger asChild>
            <Button
              leftIcon={<RiFilter3Line />}
              variant="outline"
              size="sm"
              className="h-8 w-fit text-primary"
            >
              Filter{" "}
              {filterCount > 0 && (
                <span className="ml-2">
                  <span className="text-info">({filterCount})</span>
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="flex h-[500px] w-[760px] p-0"
            align="start"
          >
            <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
              <div className="flex flex-col gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex justify-start gap-2 text-primary"
                  onClick={() => setPath("sources")}
                >
                  <RiBuilding2Line />
                  <div className="flex flex-1">External source</div>
                  {!!filter.sources?.length && (
                    <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex justify-start gap-2 text-primary"
                  onClick={() => setPath("statuses")}
                >
                  <RiPriceTagLine />
                  <div className="flex flex-1">Status</div>
                  {!!filter.active && (
                    <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex justify-start gap-2 text-primary"
                  onClick={() => setPath("statuses")}
                >
                  <RiGlobeLine />
                  <div className="flex flex-1">Country</div>
                  {!!filter.iso3CountryCodes?.length && (
                    <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                  )}
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-8 bg-primary text-primary"
                onClick={resetFilter}
              >
                Reset all filters
              </Button>
            </div>
            <div className="flex-1">
              {path === "sources" && (
                <ControlledFacetedFilterControl
                  options={sourceOptions}
                  selectedValues={filter.sources ?? []}
                  onChange={(newSelectedValues) => {
                    onFilterChange({
                      ...filter,
                      sources: newSelectedValues.length
                        ? newSelectedValues
                        : undefined,
                    });
                  }}
                />
              )}
              {path === "statuses" && (
                <ControlledFacetedFilterControl
                  options={statusOptions}
                  selectedValues={statusFilterValue}
                  onChange={(newSelectedValues) => {
                    let activeFilter: boolean | undefined;
                    if (newSelectedValues.length === 1) {
                      activeFilter = newSelectedValues[0] === "ACTIVE";
                    }
                    onFilterChange({
                      ...filter,
                      active: activeFilter,
                    });
                  }}
                />
              )}
              {path === "iso3CountryCodes" && (
                <ControlledFacetedFilterControl
                  options={countryOptions}
                  selectedValues={filter.iso3CountryCodes ?? []}
                  onChange={(newSelectedValues) => {
                    onFilterChange({
                      ...filter,
                      iso3CountryCodes: newSelectedValues.length
                        ? newSelectedValues
                        : undefined,
                    });
                  }}
                />
              )}
            </div>
          </PopoverContent>
        </Popover>

        <ControlledFactedFilterSummary
          title="External Source"
          options={sourceOptions}
          selectedValues={filter.sources ?? []}
          onClick={() => {
            setPath("sources");
            setOpen(true);
          }}
        />

        <ControlledFactedFilterSummary
          title="Status"
          options={statusOptions}
          selectedValues={statusFilterValue}
          onClick={() => {
            setPath("statuses");
            setOpen(true);
          }}
        />

        <ControlledFactedFilterSummary
          title="Country"
          options={countryOptions}
          selectedValues={filter.iso3CountryCodes ?? []}
          onClick={() => {
            setPath("iso3CountryCodes");
            setOpen(true);
          }}
        />
      </div>

      <div className="flex items-center gap-4">
        <div className="w-48">
          <Input
            ref={searchTermRef}
            defaultValue={filter.searchTerm ?? ""}
            type="search"
            placeholder="Search"
            onChange={(e) => {
              setSearchTermDebounced(e.currentTarget.value);
            }}
          />
        </div>

        <Button
          rightIcon={<X />}
          variant="ghost"
          size="sm"
          onClick={resetFilter}
          disabled={filterCount === 0}
        >
          Reset
        </Button>
      </div>
    </div>
  );
}

const formSchema = z
  .object({
    id: z.string().nullable(),
    displayName: z.string().min(3),
    externalName: z.string().min(3),
    externalId: z.string().nullish(),
    logoUrl: z.string().nullish(),
    removedAt: dateSchema.nullish(),
    iso3CountryCode: z.string().nullish(),
    source: z.enum(externalBankAccountDataSourceValues),
  })
  .superRefine((value, ctx) => {
    const logoUrlError = validateLogoUrl(value.logoUrl);

    if (logoUrlError) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: logoUrlError,
        path: ["logoUrl"],
      });
    }
  });

type FormSchema = z.input<typeof formSchema>;

function validateLogoUrl(value: string | null | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  const trimmedValue = value.trim();
  const normalizedValue = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmedValue)
    ? trimmedValue
    : `https://${trimmedValue}`;

  try {
    if (
      [
        "app.primary.tech",
        "ops.primary.tech",
        "staging-app.primary.tech",
        "staging-ops.primary.tech",
      ].includes(new URL(normalizedValue).hostname.toLowerCase())
    ) {
      return "Logo URL must not use a Primary application domain. Use static.primary.tech instead.";
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function UpsertRowDialog({
  row,
  open,
  onOpenChange,
  onSubmit,
}: {
  row: AdminExternalBankAccountDataInstitutionRowFragment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    input: UpsertExternalBankAccountDataInstitutionInput,
  ) => Promise<void>;
}) {
  const form = useAppForm({
    defaultValues: {
      id: row?.id ?? null,
      displayName: row?.displayName ?? "",
      externalName: row?.externalName ?? "",
      externalId: row?.externalId ?? null,
      logoUrl: row?.logoUrl ?? null,
      source: row?.__typename ? typenameToSource(row.__typename) : undefined,
      iso3CountryCode: row?.iso3CountryCode,
    } as Partial<FormSchema>,
    validators: {
      onSubmit: formSchema,
    },
    async onSubmit(formProps) {
      const formData = formSchema.parse(formProps.value);
      return onSubmit({
        ...formData,
        id:
          formData.id ??
          typeid("external_bank_account_data_institution").toString(),
      });
    },
  });

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form.reset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>
          <DialogHeader>{row ? "Edit Provider" : "New Provider"}</DialogHeader>
        </DialogTitle>
        <form.AppForm>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit(e);
            }}
            className="flex flex-col gap-4"
          >
            <form.AppField name="source">
              {(field) => (
                <field.SelectField label="Source" disabled={!!row}>
                  {getSourceOptions().map(({ label, value }) => (
                    <field.SelectItem key={value} value={value}>
                      {label}
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>
            <form.AppField name="displayName">
              {(field) => (
                <field.InputField
                  type="text"
                  label={
                    <span className="flex items-center gap-1">
                      Display name*
                      <Tooltip>
                        <TooltipTrigger type="button">
                          <FaInfoCircle />
                        </TooltipTrigger>
                        <TooltipContent>
                          The name shown to end users when searching for
                          Providers. Providers are de-duplicated based off their
                          display name.
                        </TooltipContent>
                      </Tooltip>
                    </span>
                  }
                />
              )}
            </form.AppField>
            <form.AppField name="externalName">
              {(field) => (
                <field.InputField
                  type="text"
                  label={
                    <span className="flex items-center gap-1">
                      External name*
                      <Tooltip>
                        <TooltipTrigger type="button">
                          <FaInfoCircle />
                        </TooltipTrigger>
                        <TooltipContent>
                          The name of the provider according to the external
                          data source
                        </TooltipContent>
                      </Tooltip>
                    </span>
                  }
                  disabled={!!row}
                />
              )}
            </form.AppField>
            <form.AppField name="externalId">
              {(field) => (
                <field.InputField
                  type="text"
                  label={
                    <span className="flex items-center gap-1">
                      External ID
                      <Tooltip>
                        <TooltipTrigger type="button">
                          <FaInfoCircle />
                        </TooltipTrigger>
                        <TooltipContent>
                          The ID of the provider according to the external data
                          source
                        </TooltipContent>
                      </Tooltip>
                    </span>
                  }
                  disabled={!!row}
                />
              )}
            </form.AppField>
            <form.AppField name="logoUrl">
              {(field) => <field.InputField type="text" label="Logo URL" />}
            </form.AppField>
            <form.AppField name="iso3CountryCode">
              {(field) => (
                <div className="flex flex-row items-end gap-2">
                  <div className="flex-1">
                    <field.SelectField label="Country">
                      {Object.entries(countries.getNames("en"))
                        .sort((a, b) => a[1].localeCompare(b[1]))
                        .map(([alpha2Code, friendlyCountry]) => (
                          <field.SelectItem
                            value={countries.toAlpha3(alpha2Code) ?? ""}
                            key={countries.toAlpha3(alpha2Code)}
                          >
                            {friendlyCountry}
                          </field.SelectItem>
                        ))}
                    </field.SelectField>
                  </div>
                  <form.Subscribe selector={(f) => f.values.iso3CountryCode}>
                    {(code) => (
                      <button
                        onClick={() => field.handleChange(null)}
                        type="button"
                        disabled={!code}
                        className="flex size-[46px] flex-row items-center justify-center rounded border text-secondary hover:bg-neutral-muted disabled:opacity-20"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </form.Subscribe>
                </div>
              )}
            </form.AppField>
            <form.SubmitButton></form.SubmitButton>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}

function typenameToSource(
  t: AdminExternalBankAccountDataInstitutionRowFragment["__typename"],
): ExternalBankAccountDataSource {
  switch (t) {
    case "AkahuExternalBankAccountDataInstitution":
      return "AKAHU";
    case "QuilttExternalBankAccountDataInstitution":
      return "QUILTT";
    case "SaltEdgeExternalBankAccountDataInstitution":
      return "SALT_EDGE";
    case "SissExternalBankAccountDataInstitution":
      return "SISS";
    case "SkriptExternalBankAccountDataInstitution":
      return "SKRIPT";
    case "StripeExternalBankAccountDataInstitution":
      return "STRIPE";
    case "MercuryExternalBankAccountDataInstitution":
      return "MERCURY";
    case "BrexExternalBankAccountDataInstitution":
      return "BREX";
    case "RampExternalBankAccountDataInstitution":
      return "RAMP";
    case "PlaidExternalBankAccountDataInstitution":
      return "PLAID";
    case "JpmcExternalBankAccountDataInstitution":
      return "JPMC";
    default:
      return neverFunc(t);
  }
}

function getSourceOptions(): {
  label: string;
  value: ExternalBankAccountDataSource;
}[] {
  return externalBankAccountDataSourceValues
    .map((s) => {
      let label = "";
      switch (s) {
        case "AKAHU":
          label = "Akahu";
          break;
        case "QUILTT":
          label = "Quiltt";
          break;
        case "SALT_EDGE":
          label = "Salt Edge";
          break;
        case "SISS":
          label = "Siss";
          break;
        case "SKRIPT":
          label = "Skript";
          break;
        case "STRIPE":
          label = "Stripe";
          break;
        case "MERCURY":
          label = "Mercury";
          break;
        case "AIRWALLEX":
          label = "Airwallex";
          break;
        case "BREX":
          label = "Brex";
          break;
        case "RAMP":
          label = "Ramp";
          break;
        case "PLAID":
          label = "Plaid";
          break;
        case "JPMC":
          label = "JPMC";
          break;
        default:
          return neverFunc(s);
      }

      return {
        value: s,
        label,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}
